import React, { useRef } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SkeletonBox } from '@eduact/ed-components'
import { useGetUserClassroomsQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { isBannedEnrollment } from '@Features/MyClassrooms/enrollment'
import { ClassroomGridCard, Icon as AbwIcon } from '@Components/AbwaabPlus'
import routes from '@Constants/Routes'

/**
 * The home's فصولي rail — the student's enrolled classrooms as the standard
 * browse cards (مشترك/محظور, no CTA), عرض الكل → /classrooms, desktop arrows.
 * Renders nothing when the student has no enrollments (the home shows the
 * explore empty-state card instead).
 */
const MyClassroomsRail: React.FC = () => {
	const [t, i18n] = useTranslation('landingPage')
	const { t: tEdu } = useTranslation('educational_info')
	const { data, isLoading, isFetching } = useGetUserClassroomsQuery()
	const navigate = useNavigate()
	const railRef = useRef<HTMLDivElement>(null)
	const isRtl = i18n.dir() === 'rtl'

	const loading = isLoading || isFetching
	const classrooms = data?.data ?? []
	if (!loading && classrooms.length === 0) return null

	const scrollRail = (dir: 'prev' | 'next') => {
		const el = railRef.current
		if (!el) return
		const amount = el.clientWidth * 0.8
		const sign = dir === 'prev' ? (isRtl ? 1 : -1) : isRtl ? -1 : 1
		el.scrollBy({ left: sign * amount, behavior: 'smooth' })
	}

	return (
		<Section>
			<HeadRow>
				<Title>{t('student_home.my_classrooms', { defaultValue: 'فصولي' })}</Title>
				<HeadControls>
					<ArrowGroup>
						<RoundArrow type="button" aria-label="prev" onClick={() => scrollRail('prev')}>
							<span style={{ transform: isRtl ? 'scaleX(-1)' : 'none', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
						<RoundArrow type="button" aria-label="next" onClick={() => scrollRail('next')}>
							<span style={{ transform: isRtl ? 'none' : 'scaleX(-1)', display: 'flex' }}>
								<AbwIcon name="chevron-left" size={15} />
							</span>
						</RoundArrow>
					</ArrowGroup>
					<SeeAll type="button" onClick={() => navigate(routes.classrooms)}>
						{t('courses.see_all', { defaultValue: 'عرض الكل' })}
						<Chevron>
							<AbwIcon name="chevron-left" size={14} />
						</Chevron>
					</SeeAll>
				</HeadControls>
			</HeadRow>
			<Rail ref={railRef}>
				{loading && Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} isLoading width="100%" height="230px" />)}
				{!loading &&
					classrooms.map((cl, index) => {
						const banned = isBannedEnrollment(cl)
						const years = cl.educationYears ?? []
						return (
							<ClassroomGridCard
								key={`${cl.id}-${index}-home`}
								title={cl.title}
								instructorName={`${cl.instructor.user.first_name} ${cl.instructor.user.last_name}`}
								yearLabels={years.map(y => tEdu(`academic_year.${y.name}`, { defaultValue: y.name }))}
								mode={cl.type === 'online' ? tEdu('online') : tEdu('center')}
								photo={cl.thumbnail}
								icon={cl.category?.icon}
								enrolled={!banned}
								enrolledLabel={tEdu('enrolled', { defaultValue: 'مشترك' })}
								banned={banned}
								bannedLabel={tEdu('banned', { defaultValue: 'محظور' })}
								onClick={() => navigate(`/classroom/${cl.label}`)}
							/>
						)
					})}
			</Rail>
		</Section>
	)
}

export default MyClassroomsRail

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
`
const HeadRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`
const Title = styled.h2`
	margin: 0;
	font: var(--fw-bold) 16px var(--font-sans);
	color: var(--text-primary);
	text-align: start;
`
const HeadControls = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: none;
`
const ArrowGroup = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: flex;
		gap: 4px;
	}
`
const RoundArrow = styled.button`
	width: 28px;
	height: 28px;
	border: 1px solid var(--border);
	border-radius: var(--r-full);
	background: var(--surface-card);
	color: var(--text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
`
const SeeAll = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 2px;
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	color: var(--blue);
`
const Chevron = styled.span`
	display: flex;
	html:not([dir='rtl']) & {
		transform: scaleX(-1);
	}
`
/* 2 cards per mobile viewport → constant 208px cards on desktop. */
const Rail = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 6px;
	scroll-snap-type: x proximity;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	> * {
		flex: 0 0 calc((100vw - 40px) / 2);
		max-width: 236px;
		scroll-snap-align: start;
	}
	@media (min-width: 1024px) {
		> * {
			flex: 0 0 208px;
			max-width: none;
		}
	}
`
